import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { type ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAudio } from '../context/AudioContext';

/**
 * Page transition: outgoing page wipes upward behind an ink mask, incoming
 * page rises from below. Pairs with a subtle whoosh from AudioContext.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  const reduce = useReducedMotion();
  const audio = useAudio();
  const lastPath = useRef(location.pathname);

  useEffect(() => {
    if (lastPath.current !== location.pathname) {
      audio.play('transition');
      lastPath.current = location.pathname;
    }
  }, [location.pathname, audio]);

  if (reduce) return <>{children}</>;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 24, clipPath: 'inset(40% 0 0 0)' }}
        animate={{ opacity: 1, y: 0, clipPath: 'inset(0% 0 0 0)' }}
        exit={{ opacity: 0, y: -10, clipPath: 'inset(0 0 40% 0)' }}
        transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
